import React from 'react';
import { Check, FileSpreadsheet, Upload } from 'lucide-react';
import type { OngletDisponible } from '../../../lib/planningExcelImport/types';

/**
 * Étape 1 de l'assistant d'import (maquette 26b) — le fichier, puis la semaine.
 *
 * Les dates affichées viennent de la ligne 2 de chaque onglet, jamais du titre : sur `S03` et `S04` le
 * titre annonce 2024 alors que la semaine est de 2025, et `S02` n'a pas de titre du tout.
 */
export function Etape1Semaine({ nomFichier, onglets, choisi, onChoisir, onFichier, lecture }: {
    nomFichier: string | null;
    onglets: OngletDisponible[];
    choisi: string | null;
    onChoisir: (nom: string) => void;
    onFichier: (fichier: File) => void;
    lecture: boolean;
}) {
    const champ = React.useRef<HTMLInputElement>(null);

    return (
        <div className="flex flex-col gap-3.5">
            <input
                ref={champ}
                type="file"
                accept=".xlsx,.xls,.xlsm"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFichier(f); e.target.value = ''; }}
            />

            {nomFichier ? (
                <div className="flex items-center gap-[11px] px-[13px] py-[11px] rounded-[10px] bg-surface-soft border border-border">
                    <span className="w-7 h-7 shrink-0 rounded-[7px] bg-state-ok-soft border border-state-ok/30 text-state-ok flex items-center justify-center">
                        <Check size={14} />
                    </span>
                    <div className="min-w-0">
                        <div className="font-mono text-xs font-medium truncate text-content">{nomFichier}</div>
                        <div className="text-[11.5px] text-content-muted">
                            {lecture ? 'lecture en cours…' : `${onglets.length} semaine${onglets.length > 1 ? 's' : ''} reconnue${onglets.length > 1 ? 's' : ''}`}
                        </div>
                    </div>
                    <button type="button" onClick={() => champ.current?.click()} className="ml-auto shrink-0 text-[11.5px] font-semibold text-content-muted hover:text-content underline">
                        Changer
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => champ.current?.click()}
                    className="flex flex-col items-center justify-center gap-2 px-4 py-8 rounded-[10px] border border-dashed border-border bg-surface-soft text-content-muted hover:border-content hover:text-content transition-colors"
                >
                    <Upload size={20} />
                    <span className="text-[12.5px] font-semibold">Choisir le classeur Excel</span>
                    <span className="text-[11.5px]">Un onglet par semaine. Le fichier est lu ici, il ne quitte pas votre navigateur.</span>
                </button>
            )}

            {onglets.length > 0 && (
                <div>
                    <label className="block text-xs font-semibold mb-2 text-content">Semaine à importer</label>
                    <div className="border border-border rounded-[10px] overflow-hidden max-h-[280px] overflow-y-auto">
                        {onglets.map((onglet) => {
                            const actif = onglet.nom === choisi;
                            return (
                                <button
                                    type="button"
                                    key={onglet.nom}
                                    onClick={() => onChoisir(onglet.nom)}
                                    className={`w-full flex items-center gap-[11px] px-[13px] py-[11px] border-b border-border/60 last:border-b-0 text-left ${actif ? 'bg-surface-soft' : 'bg-surface hover:bg-surface-soft'}`}
                                >
                                    <span className={`w-[17px] h-[17px] shrink-0 rounded-full border-[1.5px] ${actif ? 'bg-content border-content' : 'bg-surface border-border'}`} />
                                    <span className={`font-mono text-xs font-medium shrink-0 ${actif ? 'text-content' : 'text-content-muted'}`}>{onglet.nom}</span>
                                    <span className={`text-[12.5px] min-w-0 truncate ${actif ? 'text-content' : 'text-content-muted'}`}>{onglet.libelle}</span>
                                    <span className={`ml-auto text-[11px] whitespace-nowrap shrink-0 ${onglet.dejaImportee ? 'text-state-warn' : 'text-content-muted'}`}>
                                        {onglet.dejaImportee ? 'déjà importée' : `${onglet.nombreSalaries} salariés`}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <p className="mt-2 text-[11.5px] leading-relaxed text-content-muted text-pretty">
                        Les dates viennent de la ligne 2 de chaque onglet, jamais du titre : sur S03 et S04 le titre annonce
                        2024 alors que la semaine est de 2025, et S02 n'a pas de titre.
                    </p>
                </div>
            )}

            {nomFichier && !lecture && onglets.length === 0 && (
                <div className="flex items-start gap-2.5 px-[13px] py-3 rounded-[10px] bg-state-danger-soft border border-state-danger/30">
                    <FileSpreadsheet size={15} className="shrink-0 mt-px text-state-danger" />
                    <p className="text-[12px] leading-relaxed text-state-danger text-pretty">
                        Aucun onglet de semaine lisible dans ce classeur. L'import attend un onglet par semaine, nommé
                        S01 à S52, dont la ligne 2 porte les sept dates.
                    </p>
                </div>
            )}
        </div>
    );
}
